import Column from 'components/Layout/Column';
import { Control, Controller, useWatch } from 'react-hook-form';
import { CreateNoticeSchemaFormValues } from '../schema';
import { FormHelperText, styled, Typography } from '@mui/material';
import DataRow from './DataRow';
import { CurrencyCode } from 'further-types/lp/vehicle';
import RowValues from './RowValues';
import { numberToCurrencyString } from 'further-ui/utils';
import {
  allocationExceedsTotalCall,
  allocationExceedsTotalCallMessage,
} from './schema';
import { useState } from 'react';

const HelperText = styled(FormHelperText)(() => ({
  margin: 0,
}));

const StyledColumn = styled(Column)(() => ({
  maxWidth: '625px',
}));

type Props = {
  readOnly: boolean;
  control: Control<CreateNoticeSchemaFormValues>;
  currencyLcy: CurrencyCode;
  totalGeneralCall: number;
};

const UseOfFunds: React.FC<Props> = ({
  readOnly,
  currencyLcy,
  control,
  totalGeneralCall,
}) => {
  const [showAllocationValueWarning, setShowAllocationValueWarning] =
    useState(false);
  const [investments, managementFees, otherExpenses, totalCallValueLcy] =
    useWatch({
      name: [
        'useOfFunds.investments',
        'useOfFunds.managementFees',
        'useOfFunds.otherExpenses',
        'useOfFunds.totalCallValueLcy',
      ],
      control,
    });

  const valuesInvalid = allocationExceedsTotalCall({
    investments,
    managementFees,
    otherExpenses,
    totalCallValueLcy,
  });

  const handleBlur = () => {
    setShowAllocationValueWarning(true);
  };

  return (
    <StyledColumn spacing="lg">
      <Column fullWidth spacing="xs">
        <Typography variant="h6">General capital call use of funds</Typography>
        <Typography variant="body2">
          As a general capital call is included in the action, you may enter the
          planned usage split (in part or full) to assist investors with use of
          funds identification. If any general capital call amount is left
          unallocated, it will be attributed to the transaction{' '}
          <i>Call - Working capital</i> in the capital notice.
        </Typography>
      </Column>
      <DataRow readOnly={readOnly} label="Total general call">
        <Typography variant="body2" color="grey.900">
          {numberToCurrencyString(totalGeneralCall, {
            currency: currencyLcy,
          })}
        </Typography>
      </DataRow>
      <Controller
        control={control}
        name="useOfFunds.investments"
        render={({ field, fieldState }) => {
          return (
            <DataRow
              readOnly={readOnly}
              label="Investments"
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            >
              <RowValues
                totalGeneralCall={totalGeneralCall}
                readOnly={readOnly}
                onChange={field.onChange}
                currencyLcy={currencyLcy}
                value={field.value}
                onBlur={handleBlur}
              />
            </DataRow>
          );
        }}
      />
      <Controller
        control={control}
        name="useOfFunds.managementFees"
        render={({ field, fieldState }) => {
          return (
            <DataRow
              readOnly={readOnly}
              label="Management fees"
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            >
              <RowValues
                totalGeneralCall={totalGeneralCall}
                readOnly={readOnly}
                onChange={field.onChange}
                currencyLcy={currencyLcy}
                value={field.value}
                onBlur={handleBlur}
              />
            </DataRow>
          );
        }}
      />
      <Controller
        control={control}
        name="useOfFunds.otherExpenses"
        render={({ field, fieldState }) => (
          <DataRow
            readOnly={readOnly}
            label="Other expenses"
            error={fieldState.invalid}
            helperText={fieldState.error?.message}
          >
            <RowValues
              totalGeneralCall={totalGeneralCall}
              readOnly={readOnly}
              onChange={field.onChange}
              currencyLcy={currencyLcy}
              value={field.value}
              onBlur={handleBlur}
            />
          </DataRow>
        )}
      />
      <Controller
        control={control}
        name="useOfFunds.totalCallValueLcy"
        render={({ fieldState }) => {
          return fieldState.error ||
            (showAllocationValueWarning && valuesInvalid) ? (
            <HelperText error>{allocationExceedsTotalCallMessage}</HelperText>
          ) : (
            <></>
          );
        }}
      />
    </StyledColumn>
  );
};

export default UseOfFunds;
